import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  MenuItem,
  FormControl,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Field } from "formik";
import Switch from "@mui/material/Switch";
import Div from "@jumbo/shared/Div";
import { Label } from "app/pages/presentation/module/form/types";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { DSOK_POS_SUPPLIERS } from "app/utils/constants/settings";
import LinkIcon from "@mui/icons-material/Link";
import CachedIcon from "@mui/icons-material/Cached";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";
import {
  renewTeyaToken,
  teyaAuthCustomer,
  teyaDisconnectCustomer,
} from "app/services/api/teya";
import { getTeyaStores } from "app/services/api/teya";
import { initTeyaStores } from "app/services/api/teya";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import jwtAuthAxios from "app/services/auth/jwtAuth";
import { getBEUrlForAdmin } from "app/services/api/management";
import { formatDateTimeSk } from "app/utils/dateFormat";
import SonetSettlementButton from "./SonetSettlementButton";
import PreauthTestPanel from "./PreauthTestPanel";

const PosSection = ({ device, setFieldValue, values, t, posSupplier }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const [isConnected, setIsConnected] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isTestingPos, setIsTestingPos] = useState(false);
  const [isHealthChecking, setIsHealthChecking] = useState(false);
  const [confirmPosTestOpen, setConfirmPosTestOpen] = useState(false);
  const [posTestModalOpen, setPosTestModalOpen] = useState(false);
  const [posTestResult, setPosTestResult] = useState(null);
  const [posTestError, setPosTestError] = useState(null);
  const toMessage = (raw, fallback) =>
    typeof raw === "string" && raw.trim().length > 0 ? raw : fallback;
  let info;
  switch (posSupplier) {
    case "besteron":
      info = t("pages.dsokDetails.infoTexts.pos.besteron");
      break;
    case "fiskalPro":
      info = t("pages.dsokDetails.infoTexts.pos.fiskalPro");
      break;
    case "iKelpPosManager":
      info = t("pages.dsokDetails.infoTexts.pos.iKelpPosManager");
      break;
    case "teya":
      info = t("pages.dsokDetails.infoTexts.pos.teya");
      break;
    case "sonet":
      info = t("pages.dsokDetails.sonetTerminalConfiguredInDla");
      break;
    case "globalPayments":
      info = t("pages.dsokDetails.globalPaymentsTerminalConfiguredInDla");
      break;
    default:
      info = "";
  }
  const posPaymentEnabled =
    values.customer.devices[device].orderProcess?.enablePosPayment;
  const customerId = values?.customer?.customerId;
  const customerCode = values?.customer?.code;
  const teyaStores = values?.customer?.devices?.[device]?.pos?.teyaStores;
  const hasStores = Array.isArray(teyaStores) && teyaStores.length > 0;
  const selectedStoreId = values?.customer?.devices?.[device]?.pos?.teyaStoreId;
  const selectedTerminalId =
    values?.customer?.devices?.[device]?.pos?.teyaTerminalId;
  const deviceUuid = values?.customer?.devices?.[device]?.uuid;
  const beDomain =
    values?.customer?.beDomain ??
    values?.customer?.dsokSettings?.customer?.beDomain;
  const teyaBeDomain = values?.customer?.dsokSettings?.customer?.beDomain;
  const communicationType =
    values?.customer?.communicationType ??
    values?.customer?.dsokSettings?.customer?.communicationType;
  const isIkelpDla =
    posSupplier === "iKelpPosManager" && communicationType === "DLA";
  const isBesteronDla =
    posSupplier === "besteron" && communicationType === "DLA";

  const resolveDeviceId = () => {
    const currentDevice = values?.customer?.devices?.[device];
    const candidates = [
      currentDevice?.deviceId,
      currentDevice?.id,
      currentDevice?._id,
      currentDevice?.device_id,
      device,
    ];
    const found = candidates.find(
      (v) => v !== undefined && v !== null && String(v).trim().length > 0,
    );
    return found;
  };

  const resetPosTestState = React.useCallback(() => {
    setPosTestModalOpen(false);
    setPosTestResult(null);
    setPosTestError(null);
  }, []);

  React.useEffect(() => {
    if (!posTestModalOpen) return;
    const title = t("deviceTests.pos.resultTitle");

    const jsonBlock = (payload) => (
      <Box
        component="pre"
        sx={{
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: 12,
          lineHeight: 1.4,
          whiteSpace: "pre",
          overflow: "auto",
          maxHeight: 420,
          bgcolor: "background.default",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          m: 0,
        }}
      >
        {typeof payload === "string"
          ? payload
          : JSON.stringify(payload ?? null, null, 2)}
      </Box>
    );

    const closeButton = (
      <Button
        variant="contained"
        size="small"
        sx={{ boxShadow: "none", height: "38px", width: "fit-content" }}
        onClick={() => {
          resetPosTestState();
          hideDialog();
        }}
      >
        {t("buttons.close")}
      </Button>
    );

    const content = (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {posTestError ? (
          <>
            <Alert severity="error" variant="outlined">
              {t("deviceTests.pos.failed")}
            </Alert>
            {jsonBlock(posTestError)}
          </>
        ) : (
          jsonBlock(posTestResult)
        )}
        <Box display="flex" justifyContent="flex-end">
          {closeButton}
        </Box>
      </Box>
    );

    showDialog({
      variant: "default",
      title,
      content,
      onClose: resetPosTestState,
    });
  }, [
    posTestModalOpen,
    posTestError,
    posTestResult,
    t,
    showDialog,
    hideDialog,
    resetPosTestState,
  ]);

  // Global Payments: mena má vždy default EUR (aj pre skôr uložené zariadenia bez
  // nastavenej meny). tipEnabled ostáva undefined => prepínač vypnutý (default).
  const gpCurrency = values?.customer?.devices?.[device]?.pos?.currency;
  React.useEffect(() => {
    if (
      posSupplier === "globalPayments" &&
      (gpCurrency == null || gpCurrency === "")
    ) {
      setFieldValue(`customer.devices.${device}.pos.currency`, "EUR");
    }
  }, [posSupplier, gpCurrency, device, setFieldValue]);

  const handleTestPosTerminal = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const resolvedDeviceId = resolveDeviceId();
    if (!resolvedDeviceId) {
      enqueueSnackbar(t("deviceTests.pos.missingDeviceId"), {
        variant: "warning",
      });
      return;
    }

    setIsTestingPos(true);
    setPosTestResult(null);
    setPosTestError(null);

    const deviceIdPayload = Number(resolvedDeviceId);
    if (!Number.isFinite(deviceIdPayload)) {
      enqueueSnackbar(t("deviceTests.pos.invalidDeviceId"), {
        variant: "warning",
      });
      setIsTestingPos(false);
      return;
    }

    if (!customerCode || String(customerCode).trim().length === 0) {
      enqueueSnackbar(t("deviceTests.pos.missingCustomerCode"), {
        variant: "warning",
      });
      setIsTestingPos(false);
      return;
    }

    try {
      jwtAuthAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
      const { data } = await jwtAuthAxios.post("/api/v1/test/device/pos", {
        deviceId: deviceIdPayload,
        customerCode: String(customerCode).trim(),
      });
      setPosTestResult(data);
      setPosTestError(null);
      setPosTestModalOpen(true);
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        responseData?.message ??
        responseData?.error ??
        error?.message ??
        t("deviceTests.pos.failed");
      const errorDetails =
        responseData != null
          ? { message, status: error?.response?.status, data: responseData }
          : { message };
      setPosTestResult(null);
      setPosTestError(errorDetails);
      setPosTestModalOpen(true);
    } finally {
      setIsTestingPos(false);
    }
  };

  const handleHealthCheck = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const resolvedDeviceId = resolveDeviceId();
    if (!resolvedDeviceId) {
      enqueueSnackbar(t("deviceTests.pos.missingDeviceId"), {
        variant: "warning",
      });
      return;
    }
    const deviceIdPayload = Number(resolvedDeviceId);
    if (!Number.isFinite(deviceIdPayload)) {
      enqueueSnackbar(t("deviceTests.pos.invalidDeviceId"), {
        variant: "warning",
      });
      return;
    }
    if (!customerCode || String(customerCode).trim().length === 0) {
      enqueueSnackbar(t("deviceTests.pos.missingCustomerCode"), {
        variant: "warning",
      });
      return;
    }

    setIsHealthChecking(true);
    setPosTestResult(null);
    setPosTestError(null);
    try {
      jwtAuthAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
      const { data } = await jwtAuthAxios.post(
        "/api/v1/test/device/pos/handshake",
        {
          deviceId: deviceIdPayload,
          customerCode: String(customerCode).trim(),
        },
      );
      setPosTestResult(data);
      setPosTestError(null);
      setPosTestModalOpen(true);
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        responseData?.message ??
        responseData?.error ??
        error?.message ??
        t("deviceTests.pos.failed");
      const errorDetails =
        responseData != null
          ? { message, status: error?.response?.status, data: responseData }
          : { message };
      setPosTestResult(null);
      setPosTestError(errorDetails);
      setPosTestModalOpen(true);
    } finally {
      setIsHealthChecking(false);
    }
  };

  const handleConfirmPosTest = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isTestingPos || confirmPosTestOpen) return;

    setConfirmPosTestOpen(true);
    showDialog({
      variant: "confirm",
      title: t("deviceTests.pos.paymentTestTitle", {
        defaultValue: "Testovacia platba",
      }),
      content: (
        <Typography variant="body2">
          {t("deviceTests.pos.confirmText")}
        </Typography>
      ),
      onYes: async () => {
        setConfirmPosTestOpen(false);
        hideDialog();
        await handleTestPosTerminal();
      },
      onNo: () => {
        setConfirmPosTestOpen(false);
      },
    });
  };
  const normalizeId = (item) => {
    if (item == null) return undefined;
    if (typeof item === "string" || typeof item === "number") return item;
    return (
      item.id ??
      item.storeId ??
      item.store_id ??
      item.code ??
      item.value ??
      undefined
    );
  };

  const normalizeName = (item) => {
    if (item == null) return "";
    if (typeof item === "string" || typeof item === "number")
      return String(item);
    return (
      item.name ??
      item.storeName ??
      item.label ??
      item.title ??
      String(normalizeId(item) ?? "")
    );
  };

  const findStoreById = (id) => {
    if (!Array.isArray(teyaStores)) return undefined;
    return teyaStores.find((s) => String(normalizeId(s)) === String(id));
  };

  const storeTerminals = (() => {
    const store = findStoreById(selectedStoreId);
    if (!store) return [];
    const list =
      store.terminals ??
      store.devices ??
      store.posTerminals ??
      store.Terminals ??
      [];
    return Array.isArray(list) ? list : [];
  })();

  React.useEffect(() => {
    let cancelled = false;
    const loadStores = async () => {
      if (!(posPaymentEnabled && posSupplier === "teya" && customerId)) return;
      try {
        let response = await getTeyaStores(Number(customerId), teyaBeDomain);
        setIsConnected(false);
        if (response?._id) {
          setIsConnected(true);
        }
        // Compute token expiration datetime if token info is present
        try {
          const token = response?.tokenData;
          if (token?.updatedAt != null && token?.expires_in != null) {
            setExpiresAtTokenDate(token);
          }
        } catch (_e) {
          // ignore token processing errors
        }
        let list = response?.stores;
        if (cancelled) return;
        const normalized = Array.isArray(list) ? list : [];
        try {
          setFieldValue(
            `customer.devices.${device}.pos.teyaStores`,
            normalized,
          );
        } catch (_e) {
          // ignore
        }
      } catch (_e) {
        setFieldValue(`customer.devices.${device}.pos.teyaStores`, []);
      }
    };
    loadStores();
    return () => {
      cancelled = true;
    };
  }, [
    posPaymentEnabled,
    posSupplier,
    customerId,
    teyaBeDomain,
    device,
    setFieldValue,
    enqueueSnackbar,
    t,
  ]);

  const setExpiresAtTokenDate = (token) => {
    const parseDateTimeToUTC = (value) => {
      if (value instanceof Date) return value;
      if (typeof value !== "string") return new Date(NaN);
      // Expected format: YYYY-MM-DD HH:mm:ss
      const m = value.match(
        /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2}):(\d{2})$/,
      );
      if (m) {
        const [, y, mo, d, h, mi, s] = m;
        return new Date(
          Date.UTC(
            Number(y),
            Number(mo) - 1,
            Number(d),
            Number(h),
            Number(mi),
            Number(s),
          ),
        );
      }
      // Fallback to native Date if already ISO
      return new Date(value);
    };
    const updatedAtDate = parseDateTimeToUTC(token.updatedAt);
    const expiresInSec = Number(token.expires_in) || 0; // treated as milliseconds per requirement
    const expiresAtMs = updatedAtDate.getTime() + expiresInSec * 1000;
    const expiresAtDateTime = new Date(expiresAtMs);
    setExpiresAt(formatDateTimeSk(expiresAtDateTime));
  };

  const dotyPaySection =
    posSupplier === "dotypay" ? (
      <>
        <Grid item xs={12} md={12} lg={12}>
          <Alert variant="outlined" severity="info">
            {t("pages.dsokDetails.infoTexts.pos.dotyPay")}
          </Alert>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Div sx={{ mt: 1, mb: 1 }}>
            <JumboTextField
              fullWidth
              name={`customer.devices.${device}.pos.cloudIdentifier`}
              placeholder="xxxx-xxxx-xxxx"
              label={t("pages.dsokDetails.dotyPayCloudIdentifier", {
                defaultValue: "Cloud identifier",
              })}
            />
          </Div>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Div sx={{ mt: 1, mb: 1 }}>
            <JumboTextField
              fullWidth
              name={`customer.devices.${device}.pos.poiId`}
              placeholder="tid0001"
              label={t("pages.dsokDetails.poiId")}
            />
          </Div>
        </Grid>
        <Grid item xs={12} md={4} lg={3}>
          <Div sx={{ mt: 1, mb: 1 }}>
            <JumboTextField
              fullWidth
              name={`customer.devices.${device}.pos.saleId`}
              placeholder="DSOK_KIOSK_01"
              label={t("pages.dsokDetails.dotyPaySaleId", {
                defaultValue: "Sale ID",
              })}
            />
          </Div>
        </Grid>
      </>
    ) : null;

  return (
    <>
      <Grid item xs={12} md={12} lg={12}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.pos")}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.orderProcess.enablePosPayment`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.orderProcess.enablePosPayment`,
                    e.target.checked,
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.enablePosPayment")}
                color="primary"
                helpText={t("pages.dsokDetails.enablePosPaymentHelp")}
              />
            }
            checked={
              values.customer.devices[device].orderProcess?.enablePosPayment
            }
          />
        </Div>
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.orderProcess.enableCashMachine`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.orderProcess.enableCashMachine`,
                    e.target.checked,
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.enableCashMachine", {
                  defaultValue: "Hotovostný automat (ALBACON)",
                })}
                color="primary"
                helpText={t("pages.dsokDetails.enableCashMachineHelp", {
                  defaultValue:
                    "Povolí platbu hotovostným automatom na tomto kiosku. Stroj sa konfiguruje lokálne v DLA.",
                })}
              />
            }
            checked={
              values.customer.devices[device].orderProcess?.enableCashMachine
            }
          />
        </Div>
      </Grid>
      {posPaymentEnabled && (
        <>
          {isIkelpDla ? (
            <>
              <Grid item xs={12} md={3} lg={2}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    required
                    select
                    InputLabelProps={{ required: false }}
                    name={`customer.devices.${device}.pos.supplier`}
                    label={
                      <Label text={t("pages.dsokDetails.supplier")} required />
                    }
                  >
                    {DSOK_POS_SUPPLIERS.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </JumboTextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Div sx={{ mt: 2.25, mb: 1, mx: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={t("deviceTests.pos.paymentTestTitle", {
                      defaultValue: "Testovacia platba",
                    })}
                    placement="top-start"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={isTestingPos}
                        onClick={handleConfirmPosTest}
                        aria-label={t("deviceTests.pos.paymentTestTitle", {
                          defaultValue: "Testovacia platba",
                        })}
                        sx={{
                          height: 38,
                          width: 38,
                        }}
                      >
                        {isTestingPos ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PointOfSaleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Div>
              </Grid>
              <Grid item xs={12} md={12} lg={12}></Grid>
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name={`customer.devices.${device}.pos.serialNumber`}
                    label={t("pages.dsokDetails.posSerialNumber")}
                  />
                </Div>
              </Grid>
              {posSupplier === "sonet" && (
                <Grid item xs={12} md={2} lg={2}>
                  <Div sx={{ mt: 1, mb: 1 }}>
                    <JumboTextField
                      fullWidth
                      name={`customer.devices.${device}.pos.acquirerId`}
                      placeholder={t("devices.acquirerIdPlaceholder")}
                      inputProps={{ maxLength: 2 }}
                      label={
                        <Label
                          text={t("devices.acquirerId")}
                          helpText={t("devices.acquirerIdHelp")}
                        />
                      }
                    />
                  </Div>
                </Grid>
              )}
              {dotyPaySection}
            </>
          ) : isBesteronDla ? (
            <>
              <Grid item xs={12} md={3} lg={2}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    required
                    select
                    InputLabelProps={{ required: false }}
                    name={`customer.devices.${device}.pos.supplier`}
                    label={
                      <Label text={t("pages.dsokDetails.supplier")} required />
                    }
                  >
                    {DSOK_POS_SUPPLIERS.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </JumboTextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Div sx={{ mt: 2.25, mb: 1, mx: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={t("deviceTests.pos.paymentTestTitle", {
                      defaultValue: "Testovacia platba",
                    })}
                    placement="top-start"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={isTestingPos}
                        onClick={handleConfirmPosTest}
                        aria-label={t("deviceTests.pos.paymentTestTitle", {
                          defaultValue: "Testovacia platba",
                        })}
                        sx={{
                          height: 38,
                          width: 38,
                        }}
                      >
                        {isTestingPos ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PointOfSaleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Div>
              </Grid>
              <Grid item xs={12} md={12} lg={12}></Grid>
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    required
                    name={`customer.devices.${device}.pos.poiId`}
                    label={t("pages.dsokDetails.poiId")}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name={`customer.devices.${device}.pos.username`}
                    label={t("pages.login.username")}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name={`customer.devices.${device}.pos.password`}
                    label={t("pages.login.password")}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={12} lg={12}></Grid>
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name={`customer.devices.${device}.pos.serialNumber`}
                    label={t("pages.dsokDetails.posSerialNumber")}
                  />
                </Div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={3} lg={2}>
                <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name={`customer.devices.${device}.pos.useHTTPS`}
                        component={Switch}
                        onChange={(e) =>
                          setFieldValue(
                            `customer.devices.${device}.pos.useHTTPS`,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t("pages.dsokDetails.useHTTPS")}
                    checked={values.customer.devices[device].pos?.useHTTPS}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name={`customer.devices.${device}.pos.ekasa`}
                        component={Switch}
                        onChange={(e) =>
                          setFieldValue(
                            `customer.devices.${device}.pos.ekasa`,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t("pages.dsokDetails.ekasa")}
                    checked={values.customer.devices[device].pos?.ekasa}
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    required
                    select
                    InputLabelProps={{ required: false }}
                    name={`customer.devices.${device}.pos.supplier`}
                    label={
                      <Label text={t("pages.dsokDetails.supplier")} required />
                    }
                  >
                    {DSOK_POS_SUPPLIERS.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </JumboTextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Div sx={{ mt: 2.25, mb: 1, mx: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={t("deviceTests.pos.paymentTestTitle", {
                      defaultValue: "Testovacia platba",
                    })}
                    placement="top-start"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={isTestingPos}
                        onClick={handleConfirmPosTest}
                        aria-label={t("deviceTests.pos.paymentTestTitle", {
                          defaultValue: "Testovacia platba",
                        })}
                        sx={{
                          height: 38,
                          width: 38,
                        }}
                      >
                        {isTestingPos ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PointOfSaleIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {posSupplier === "globalPayments" && (
                    <Tooltip
                      title={t("deviceTests.pos.healthCheckTitle", {
                        defaultValue: "Handshake / health check",
                      })}
                      placement="top-start"
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={isHealthChecking}
                          onClick={handleHealthCheck}
                          aria-label={t("deviceTests.pos.healthCheckTitle", {
                            defaultValue: "Handshake / health check",
                          })}
                          sx={{ height: 38, width: 38 }}
                        >
                          {isHealthChecking ? (
                            <CircularProgress size={18} />
                          ) : (
                            <NetworkCheckIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {posSupplier === "sonet" && (
                    <SonetSettlementButton deviceUuid={deviceUuid} beDomain={beDomain} customerId={customerId} disabled />
                  )}
                </Div>
              </Grid>
              {posSupplier === "globalPayments" && (
                <Grid item xs={12}>
                  <PreauthTestPanel
                    customerCode={customerCode}
                    deviceId={resolveDeviceId()}
                  />
                </Grid>
              )}
              {posSupplier === "globalPayments" && (
                <>
                  <Grid item xs={12} md={3} lg={2}>
                    <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                      <JumboTextField
                        select
                        name={`customer.devices.${device}.pos.currency`}
                        label={
                          <Label
                            text={t("pages.dsokDetails.gpCurrency", {
                              defaultValue: "Mena terminálu",
                            })}
                          />
                        }
                      >
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="CZK">CZK</MenuItem>
                      </JumboTextField>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Field
                            name={`customer.devices.${device}.pos.tipEnabled`}
                            component={Switch}
                            onChange={(e) =>
                              setFieldValue(
                                `customer.devices.${device}.pos.tipEnabled`,
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDetails.gpTipEnabled", {
                          defaultValue: "Povoliť sprepitné",
                        })}
                        checked={
                          !!values.customer.devices[device].pos?.tipEnabled
                        }
                      />
                    </Div>
                  </Grid>
                </>
              )}
              {posSupplier === "teya" && (
                <>
                  <Grid item xs={12} md={1} lg={0.5}>
                    <Div sx={{ mt: 2.5, mb: 1, mx: 0.5 }}>
                      <Tooltip
                        title={
                          isConnected
                            ? t("pages.dsokDetails.disconnect")
                            : t("pages.dsokDetails.connect")
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isConnected) {
                              showDialog({
                                variant: "confirm",
                                title: t(
                                  "pages.dsokDetails.disconnectConfirmTitle",
                                  {
                                    defaultValue:
                                      "Do you really want to disconnect Teya?",
                                  },
                                ),
                                onYes: async () => {
                                  hideDialog();
                                  try {
                                    const resp = await teyaDisconnectCustomer(
                                      Number(customerId),
                                      values?.customer?.beDomain,
                                    );
                                    if (resp?.code === 200) {
                                      try {
                                        setIsConnected(false);
                                        setFieldValue(
                                          `customer.devices.${device}.pos.teyaStores`,
                                          [],
                                        );
                                        setFieldValue(
                                          `customer.devices.${device}.pos.teyaStoreId`,
                                          "",
                                        );
                                        setFieldValue(
                                          `customer.devices.${device}.pos.teyaTerminalId`,
                                          "",
                                        );
                                      } catch (_e) {
                                        // ignore form update errors
                                      }
                                      enqueueSnackbar(
                                        t(
                                          "pages.dsokDetails.disconnectSuccess",
                                          {
                                            defaultValue:
                                              "Disconnected successfully",
                                          },
                                        ),
                                        { variant: "success" },
                                      );
                                    } else {
                                      const errorText = toMessage(
                                        resp?.message,
                                        t("pages.dsokDetails.disconnectError", {
                                          defaultValue: "Disconnect failed",
                                        }),
                                      );
                                      enqueueSnackbar(errorText, {
                                        variant: "error",
                                      });
                                    }
                                  } catch (_e) {
                                    enqueueSnackbar(
                                      t("pages.dsokDetails.disconnectError", {
                                        defaultValue: "Disconnect failed",
                                      }),
                                      { variant: "error" },
                                    );
                                  }
                                },
                                onNo: () => {},
                              });
                              return;
                            }

                            // Connect flow
                            try {
                              const authResult = await teyaAuthCustomer(
                                Number(customerId),
                                values?.customer?.beDomain,
                              );
                              const { message } = authResult || {};
                              const { verification_url_complete } =
                                message || {};
                              if (
                                authResult?.code === 200 &&
                                verification_url_complete
                              ) {
                                setIsConnected(true);
                                window.open(
                                  verification_url_complete,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              } else {
                                const errorText = toMessage(
                                  message,
                                  t("pages.dsokDetails.connectError", {
                                    defaultValue: "Connect error",
                                  }),
                                );
                                enqueueSnackbar(errorText, {
                                  variant: "error",
                                });
                              }
                            } catch (_e) {
                              enqueueSnackbar(
                                t("pages.dsokDetails.connectError", {
                                  defaultValue: "Connect error",
                                }),
                                { variant: "error" },
                              );
                            }
                          }}
                        >
                          <LinkIcon
                            sx={{
                              color: isConnected
                                ? "error.main"
                                : "primary.main",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Div>
                  </Grid>
                  {isConnected && !hasStores && (
                    <Grid item xs={12} md={1} lg={0.5}>
                      <Div sx={{ mt: 2.5, mb: 1, mx: 0.5 }}>
                        <Tooltip
                          title={t("pages.dsokDetails.initStores", {
                            defaultValue: "Init stores",
                          })}
                        >
                          <IconButton
                            size="small"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                const list = await initTeyaStores(
                                  Number(customerId),
                                  values?.customer?.beDomain,
                                );
                                const normalized = Array.isArray(list)
                                  ? list
                                  : [];
                                setIsConnected(true);
                                setFieldValue(
                                  `customer.devices.${device}.pos.teyaStores`,
                                  normalized,
                                );
                                enqueueSnackbar(
                                  t("pages.dsokDetails.teyaStoresInitialized", {
                                    defaultValue: "Stores initialized",
                                  }),
                                  { variant: "success" },
                                );
                              } catch (_e) {
                                enqueueSnackbar(
                                  t("pages.dsokDetails.teyaStoresInitError", {
                                    defaultValue: "Failed to init stores",
                                  }),
                                  { variant: "error" },
                                );
                              }
                            }}
                          >
                            <CachedIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      </Div>
                    </Grid>
                  )}
                </>
              )}
              {info ? (
                <Grid item xs={12} md={12} lg={12}>
                  <Alert variant="outlined" severity="info">
                    {info}
                  </Alert>
                </Grid>
              ) : null}
              <Grid item xs={12} md={12} lg={12}></Grid>
              {Array.isArray(teyaStores) &&
                teyaStores.length > 0 &&
                posSupplier === "teya" && (
                  <>
                    <Grid item xs={12} md={3} lg={3}>
                      <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                        <JumboTextField
                          select
                          fullWidth
                          name={`customer.devices.${device}.pos.teyaStoreId`}
                          label={t("pages.dsokDetails.teyaStore", {
                            defaultValue: "Teya Store",
                          })}
                          value={selectedStoreId ?? ""}
                          onChange={(e) => {
                            const newStoreId = e.target.value;
                            setFieldValue(
                              `customer.devices.${device}.pos.teyaStoreId`,
                              newStoreId,
                            );
                            // Reset terminal on store change
                            setFieldValue(
                              `customer.devices.${device}.pos.teyaTerminalId`,
                              "",
                            );
                          }}
                        >
                          {teyaStores.map((store) => {
                            const id = normalizeId(store);
                            const name = normalizeName(store);
                            return (
                              <MenuItem key={String(id)} value={String(id)}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </JumboTextField>
                      </FormControl>
                    </Grid>
                    {Array.isArray(storeTerminals) &&
                      storeTerminals.length > 0 &&
                      posSupplier === "teya" && (
                        <Grid item xs={12} md={3} lg={3}>
                          <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                            <JumboTextField
                              select
                              fullWidth
                              name={`customer.devices.${device}.pos.teyaTerminalId`}
                              label={t("pages.dsokDetails.teyaTerminal", {
                                defaultValue: "Teya Terminal",
                              })}
                              value={selectedTerminalId ?? ""}
                              onChange={(e) =>
                                setFieldValue(
                                  `customer.devices.${device}.pos.teyaTerminalId`,
                                  e.target.value,
                                )
                              }
                            >
                              {storeTerminals.map((terminal) => {
                                const id =
                                  terminal?.terminal_id ??
                                  terminal?.id ??
                                  terminal?.code ??
                                  normalizeId(terminal);
                                const name =
                                  terminal?.terminal_name +
                                    ", SN: " +
                                    terminal?.serial_number ||
                                  normalizeName(terminal);
                                return (
                                  <MenuItem key={String(id)} value={String(id)}>
                                    {name}
                                  </MenuItem>
                                );
                              })}
                            </JumboTextField>
                          </FormControl>
                        </Grid>
                      )}
                    {isConnected && hasStores && expiresAt && (
                      <>
                        <Grid item xs={12} md={12} lg={12}></Grid>
                        <Grid item xs={12} md={12} lg={4}>
                          <Alert variant="outlined" severity="warning">
                            {t("pages.dsokDetails.tokenExpiresAt", {
                              defaultValue: "Token expired at: ",
                            })}{" "}
                            {expiresAt}
                          </Alert>
                        </Grid>
                        <Grid item xs={12} md={12} lg={1}>
                          <Tooltip
                            title={t("pages.dsokDetails.refreshToken", {
                              defaultValue: "Refresh Token",
                            })}
                          >
                            <IconButton
                              size="small"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                showDialog({
                                  variant: "confirm",
                                  title: t(
                                    "pages.dsokDetails.confirmRefreshToken",
                                    {
                                      defaultValue:
                                        "Are you sure you want to refresh the token?",
                                    },
                                  ),
                                  onYes: async () => {
                                    hideDialog();
                                    try {
                                      const newTokenInfo = await renewTeyaToken(
                                        Number(customerId),
                                        values?.customer?.beDomain,
                                      );
                                      if (newTokenInfo) {
                                        setExpiresAtTokenDate(newTokenInfo);
                                        enqueueSnackbar(
                                          t(
                                            "pages.dsokDetails.tokenRefreshed",
                                            {
                                              defaultValue:
                                                "Token refreshed successfully",
                                            },
                                          ),
                                          { variant: "success" },
                                        );
                                      }
                                    } catch (_e) {
                                      enqueueSnackbar(
                                        t(
                                          "pages.dsokDetails.tokenRefreshError",
                                          {
                                            defaultValue:
                                              "Failed to refresh token",
                                          },
                                        ),
                                        { variant: "error" },
                                      );
                                    }
                                  },
                                  onNo: () => {
                                    hideDialog();
                                  },
                                });
                              }}
                            >
                              <CachedIcon color="warning" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              {posSupplier !== "teya" &&
                posSupplier !== "sonet" &&
                posSupplier !== "globalPayments" &&
                posSupplier !== "dotypay" && (
                  <>
                    <Grid item xs={12} md={3} lg={3}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          required
                          fullWidth
                          name={`customer.devices.${device}.pos.ip`}
                          label={t("pages.dsokDetails.ip")}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={2} lg={2}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          fullWidth
                          required
                          name={`customer.devices.${device}.pos.localPort`}
                          label={t("pages.dsokDetails.localPort")}
                        />
                      </Div>
                    </Grid>
                    <Grid item xs={12} md={2} lg={2}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          required
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          name={`customer.devices.${device}.pos.serverPort`}
                          label={t("pages.dsokDetails.serverPort")}
                        />
                      </Div>
                    </Grid>
                  </>
                )}
              <Grid item xs={12} md={12} lg={12}></Grid>
              {posSupplier === "besteron" && (
                <>
                  <Grid item xs={12} md={2} lg={2}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        required
                        name={`customer.devices.${device}.pos.poiId`}
                        label={t("pages.dsokDetails.poiId")}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={2} lg={2}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name={`customer.devices.${device}.pos.username`}
                        label={t("pages.login.username")}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={2} lg={2}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name={`customer.devices.${device}.pos.password`}
                        label={t("pages.login.password")}
                      />
                    </Div>
                  </Grid>
                </>
              )}
              {posSupplier === "iKelpPosManager" && (
                <>
                  <Grid item xs={12} md={4} lg={4}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        InputLabelProps={{ required: false }}
                        name={`customer.devices.${device}.pos.posCode`}
                        label={
                          <Label
                            text={t("pages.dsokDetails.posCode")}
                            color="primary"
                            helpText={t("pages.dsokDetails.posCodeHelpText")}
                            required
                          />
                        }
                      />
                    </Div>
                  </Grid>
                </>
              )}
              {posSupplier === "fiskalPro" && (
                <>
                  <Grid item xs={12} md={4} lg={2}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name={`customer.devices.${device}.pos.serialNumber`}
                        label={t("pages.dsokDetails.posSerialNumber")}
                      />
                    </Div>
                  </Grid>
                </>
              )}
              {dotyPaySection}
              <Grid item xs={12} md={2} lg={2}>
                <Div sx={{ mt: 1, mb: 1 }}>
                  <JumboTextField
                    fullWidth
                    name={`customer.devices.${device}.pos.serialNumber`}
                    label={t("pages.dsokDetails.posSerialNumber")}
                  />
                </Div>
              </Grid>
              {posSupplier === "sonet" && (
                <Grid item xs={12} md={2} lg={2}>
                  <Div sx={{ mt: 1, mb: 1 }}>
                    <JumboTextField
                      fullWidth
                      name={`customer.devices.${device}.pos.acquirerId`}
                      placeholder={t("devices.acquirerIdPlaceholder")}
                      inputProps={{ maxLength: 2 }}
                      label={
                        <Label
                          text={t("devices.acquirerId")}
                          helpText={t("devices.acquirerIdHelp")}
                        />
                      }
                    />
                  </Div>
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default PosSection;
